import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { 
  format, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay,
  addMonths, subMonths, startOfWeek, endOfWeek, addWeeks, subWeeks, addDays
} from 'date-fns';
import { es } from 'date-fns/locale';
import { Plus, Calendar as CalendarIcon, CalendarDays, CalendarRange, LayoutGrid, Search, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useExamTypes } from '../hooks/useExamTypes';
import {
  DayView, WeekView, MonthView, AppointmentModal, AppointmentDetail,
  API_URL, initialFormData
} from '../components/agenda';

const Agenda = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [appointments, setAppointments] = useState([]);
  const [selectedDateAppointments, setSelectedDateAppointments] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [calendarConnected, setCalendarConnected] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [appointmentToCancel, setAppointmentToCancel] = useState(null);
  const [viewMode, setViewMode] = useState('day');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [formData, setFormData] = useState({
    ...initialFormData,
    date: format(new Date(), 'yyyy-MM-dd')
  });
  
  // Dynamic exam types from API
  const { examTypeNames, examTypes: examTypesData } = useExamTypes(token);

  useEffect(() => {
    fetchAppointments();
    checkCalendarStatus();
  }, [currentDate]);

  const checkCalendarStatus = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/calendar/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCalendarConnected(response.data.connected);
      if (response.data.connected) {
        setFormData(prev => ({ ...prev, sync_google_calendar: true }));
      }
    } catch (error) {
      console.error('Error checking calendar status:', error);
    }
  };

  useEffect(() => {
    const dateStr = format(selectedDate, 'yyyy-MM-dd');
    const filtered = appointments.filter(apt => apt.date === dateStr);
    setSelectedDateAppointments(filtered.sort((a, b) => (a.hora_inicio || '').localeCompare(b.hora_inicio || '')));
  }, [selectedDate, appointments]);

  const fetchAppointments = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/appointments`);
      setAppointments(response.data);
    } catch (error) {
      console.error('Error fetching appointments:', error);
      toast.error('Error al cargar las citas');
    } finally {
      setLoading(false);
    }
  };

  // Navigation handlers
  const handlePrevMonth = () => setCurrentDate(subMonths(currentDate, 1));
  const handleNextMonth = () => setCurrentDate(addMonths(currentDate, 1));
  const handlePrevWeek = () => {
    setCurrentDate(subWeeks(currentDate, 1));
    setSelectedDate(subWeeks(selectedDate, 1));
  };
  const handleNextWeek = () => {
    setCurrentDate(addWeeks(currentDate, 1));
    setSelectedDate(addWeeks(selectedDate, 1));
  };
  const handleToday = () => {
    setCurrentDate(new Date());
    setSelectedDate(new Date());
  };
  const handleDayClick = (day) => setSelectedDate(day);
  const handleDaySelect = (day) => {
    setSelectedDate(day);
    setViewMode('day');
  };

  // Get days for views
  const getDaysInMonth = () => {
    const start = startOfMonth(currentDate);
    const end = endOfMonth(currentDate);
    return eachDayOfInterval({ start, end });
  };

  const days = getDaysInMonth();
  const firstDayOfMonth = startOfMonth(currentDate).getDay();
  const emptyDays = Array(firstDayOfMonth).fill(null);

  const weekDays = useMemo(() => {
    const start = startOfWeek(currentDate, { weekStartsOn: 0 });
    return Array.from({ length: 7 }, (_, i) => addDays(start, i));
  }, [currentDate]);

  const weekAppointments = useMemo(() => {
    const result = {};
    weekDays.forEach(day => {
      const dateStr = format(day, 'yyyy-MM-dd');
      result[dateStr] = appointments
        .filter(apt => apt.date === dateStr)
        .sort((a, b) => (a.hora_inicio || '').localeCompare(b.hora_inicio || ''));
    });
    return result;
  }, [weekDays, appointments]);

  const monthAppointments = useMemo(() => {
    const result = {};
    appointments.forEach(apt => {
      if (!result[apt.date]) result[apt.date] = [];
      result[apt.date].push(apt);
    });
    Object.keys(result).forEach(date => {
      result[date].sort((a, b) => (a.hora_inicio || '').localeCompare(b.hora_inicio || ''));
    });
    return result;
  }, [appointments]);

  // Modal handlers
  const openNewAppointmentModal = () => {
    setIsEditing(false);
    setFormData({
      ...initialFormData,
      date: format(selectedDate, 'yyyy-MM-dd'),
      sync_google_calendar: calendarConnected,
    });
    setIsModalOpen(true);
  };

  const openAppointmentDetail = (appointment) => {
    setSelectedAppointment(appointment);
    setIsDetailModalOpen(true);
  };

  const openEditModal = () => {
    setIsEditing(true);
    setFormData({
      patient_name: selectedAppointment.patient_name,
      fecha_nacimiento: selectedAppointment.fecha_nacimiento,
      edad: selectedAppointment.edad,
      telefono: selectedAppointment.telefono,
      institucion: selectedAppointment.institucion,
      medico_referente: selectedAppointment.medico_referente || '',
      tipo_examen: selectedAppointment.tipo_examen,
      duracion: selectedAppointment.duracion,
      date: selectedAppointment.date,
      hora_inicio: selectedAppointment.hora_inicio,
      hora_fin: selectedAppointment.hora_fin,
      notes: selectedAppointment.notes || '',
      status: selectedAppointment.status,
    });
    setIsDetailModalOpen(false);
    setIsModalOpen(true);
  };

  // Form validation and submit
  const validateForm = () => {
    if (!formData.patient_name) { toast.error('El nombre del paciente es requerido'); return false; }
    if (!formData.fecha_nacimiento) { toast.error('La fecha de nacimiento es requerida'); return false; }
    if (!formData.telefono || formData.telefono.replace(/\D/g, '').length !== 8) { toast.error('El teléfono debe tener 8 dígitos'); return false; }
    if (!formData.institucion) { toast.error('La institución es requerida'); return false; }
    if (!formData.tipo_examen) { toast.error('El tipo de examen es requerido'); return false; }
    if (!formData.hora_inicio) { toast.error('La hora de inicio es requerida'); return false; }
    if (formData.hora_fin > '15:30') { toast.error('La cita no puede terminar después de las 3:30 PM'); return false; }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setSaving(true);
    try {
      if (isEditing && selectedAppointment) {
        await axios.put(`${API_URL}/api/appointments/${selectedAppointment.id}`, formData);
        toast.success('Cita actualizada exitosamente');
      } else {
        await axios.post(`${API_URL}/api/appointments`, formData);
        toast.success('Cita creada exitosamente');
      }
      setIsModalOpen(false);
      setIsEditing(false);
      setSelectedAppointment(null);
      fetchAppointments();
    } catch (error) {
      console.error('Error saving appointment:', error);
      toast.error(error.response?.data?.detail || 'Error al guardar la cita');
    } finally {
      setSaving(false);
    }
  };

  // Status handlers
  const handleStatusChange = async (appointmentId, newStatus, closeModal = false) => {
    try {
      await axios.put(`${API_URL}/api/appointments/${appointmentId}`, { status: newStatus });
      toast.success('Estado actualizado');
      if (closeModal) setIsDetailModalOpen(false);
      fetchAppointments();
      if (selectedAppointment && selectedAppointment.id === appointmentId) {
        setSelectedAppointment(prev => ({ ...prev, status: newStatus }));
      }
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Error al actualizar el estado');
    }
  };

  const openCancelDialog = () => {
    if (selectedAppointment) {
      setAppointmentToCancel(selectedAppointment);
      setCancelDialogOpen(true);
    }
  };

  const confirmCancelAppointment = async () => {
    if (!appointmentToCancel) return;
    try {
      await axios.put(`${API_URL}/api/appointments/${appointmentToCancel.id}`, { status: 'CANCELADA' });
      toast.warning('Cita cancelada');
      setCancelDialogOpen(false);
      setAppointmentToCancel(null);
      setIsDetailModalOpen(false);
      fetchAppointments();
    } catch (error) {
      console.error('Error cancelling appointment:', error);
      toast.error('Error al cancelar la cita');
    }
  };

  const handleMarkCompleted = () => {
    handleStatusChange(selectedAppointment.id, 'COMPLETADA', true);
  };

  const handleNoShow = () => {
    handleStatusChange(selectedAppointment.id, 'NO_ASISTIO', true);
  };

  const handleReschedule = () => {
    handleStatusChange(selectedAppointment.id, 'REPROGRAMADA', true);
    toast.info('Cita marcada como reprogramada. Edite la cita para asignar nueva fecha/hora.');
  };

  // Handle drag & drop reschedule
  const handleDragReschedule = async (appointmentId, newSchedule) => {
    try {
      await axios.put(`${API_URL}/api/appointments/${appointmentId}`, {
        date: newSchedule.date,
        hora_inicio: newSchedule.hora_inicio,
        hora_fin: newSchedule.hora_fin
      });
      toast.success(`Cita reprogramada a ${newSchedule.date} ${newSchedule.hora_inicio}`);
      fetchAppointments();
    } catch (error) {
      console.error('Error rescheduling appointment:', error);
      toast.error(error.response?.data?.detail || 'Error al reprogramar la cita');
    }
  };

  // Search appointments by patient name, phone, or exam type
  const handleSearch = (query) => {
    setSearchQuery(query);
    if (query.trim().length < 2) {
      setSearchResults([]);
      setShowSearchResults(false);
      return;
    }
    
    const lowerQuery = query.toLowerCase();
    const results = appointments.filter(apt => 
      apt.patient_name?.toLowerCase().includes(lowerQuery) ||
      apt.telefono?.includes(query) ||
      apt.tipo_examen?.toLowerCase().includes(lowerQuery)
    ).slice(0, 10); // Limit to 10 results
    
    setSearchResults(results);
    setShowSearchResults(true);
  };

  // Navigate to appointment date when clicking search result
  const handleSearchResultClick = (appointment) => {
    const appointmentDate = new Date(appointment.date + 'T12:00:00');
    setCurrentDate(appointmentDate);
    setSelectedDate(appointmentDate);
    setSearchQuery('');
    setShowSearchResults(false);
    // Open the appointment detail
    openAppointmentDetail(appointment);
  };

  const handleCreatePatientRecord = () => {
    // Build examenes from appointment's examenes or tipo_examen
    let examenes = [];
    if (selectedAppointment.examenes && selectedAppointment.examenes.length > 0) {
      examenes = selectedAppointment.examenes;
    } else if (selectedAppointment.tipo_examen) {
      // Support for old format with single tipo_examen
      const tipos = selectedAppointment.tipo_examen.split(',').map(t => t.trim());
      examenes = tipos.map(t => ({ tipo_examen: t, duracion: Math.floor(selectedAppointment.duracion / tipos.length) || 30 }));
    }
    
    const patientData = {
      fecha: selectedAppointment.date,
      nombre: selectedAppointment.patient_name,
      fecha_nacimiento: selectedAppointment.fecha_nacimiento || '',
      edad: selectedAppointment.edad,
      telefono: selectedAppointment.telefono,
      correo: selectedAppointment.correo || '',
      nit: selectedAppointment.nit || '',
      medico_referente: selectedAppointment.medico_referente || '',
      tipo_examen: selectedAppointment.tipo_examen,
      examenes: examenes,
      duracion: selectedAppointment.duracion,
      institucion: selectedAppointment.institucion || '',
      // IGSS fields
      igss_afiliacion: selectedAppointment.igss_afiliacion || '',
      igss_codigo: selectedAppointment.igss_codigo || '',
      igss_nog: selectedAppointment.igss_nog || '',
      igss_spg: selectedAppointment.igss_spg || '',
      igss_unidad_referente: selectedAppointment.igss_unidad_referente || '',
      appointmentId: selectedAppointment.id,
    };
    setIsDetailModalOpen(false);
    navigate('/pacientes', { state: { fromAppointment: true, appointmentData: patientData } });
  };

  return (
    <div className="space-y-6" data-testid="agenda-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="font-heading text-2xl font-bold text-slate-900">Agenda de Citas</h2>
          <p className="text-sm text-slate-500 mt-1">Gestiona las citas y visualiza el calendario</p>
        </div>
        <div className="flex items-center gap-3">
          {/* Search */}
          <div className="relative">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                type="text"
                placeholder="Buscar paciente, teléfono..."
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                className="pl-9 pr-8 w-56 h-9 text-sm"
                data-testid="agenda-search-input"
              />
              {searchQuery && (
                <button
                  onClick={() => { setSearchQuery(''); setShowSearchResults(false); }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            {/* Search Results Dropdown */}
            {showSearchResults && searchResults.length > 0 && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg z-50 max-h-80 overflow-y-auto">
                <div className="p-2 border-b border-slate-100 bg-slate-50">
                  <span className="text-xs font-medium text-slate-500">
                    {searchResults.length} resultado(s) encontrado(s)
                  </span>
                </div>
                {searchResults.map((apt) => (
                  <div
                    key={apt.id}
                    onClick={() => handleSearchResultClick(apt)}
                    className="p-3 hover:bg-slate-50 cursor-pointer border-b border-slate-100 last:border-0"
                  >
                    <div className="flex items-center justify-between">
                      <p className="font-medium text-sm text-slate-800">{apt.patient_name}</p>
                      <span className="text-xs font-mono text-slate-500">
                        {format(new Date(apt.date + 'T12:00:00'), 'dd/MM/yy')}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs text-slate-500">{apt.hora_inicio}</span>
                      <span className="text-xs text-slate-400">•</span>
                      <span className="text-xs text-slate-500">{apt.tipo_examen}</span>
                      <span className="text-xs text-slate-400">•</span>
                      <span className="text-xs text-slate-500">{apt.telefono}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {showSearchResults && searchResults.length === 0 && searchQuery.length >= 2 && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg z-50 p-4 text-center">
                <p className="text-sm text-slate-500">No se encontraron citas</p>
              </div>
            )}
          </div>

          {/* View Mode Tabs */}
          <div className="flex bg-slate-100 rounded-lg p-1">
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setViewMode('day')}
              className={`px-3 ${viewMode === 'day' ? 'bg-[#1a5192] text-white shadow-sm hover:bg-[#1a5192]/90' : 'hover:bg-slate-200 text-slate-600'}`}
              data-testid="view-day-button"
            >
              <CalendarIcon className="w-4 h-4 mr-1" />
              Día
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setViewMode('week')}
              className={`px-3 ${viewMode === 'week' ? 'bg-[#1a5192] text-white shadow-sm hover:bg-[#1a5192]/90' : 'hover:bg-slate-200 text-slate-600'}`}
              data-testid="view-week-button"
            >
              <CalendarRange className="w-4 h-4 mr-1" />
              Semana
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setViewMode('month')}
              className={`px-3 ${viewMode === 'month' ? 'bg-[#1a5192] text-white shadow-sm hover:bg-[#1a5192]/90' : 'hover:bg-slate-200 text-slate-600'}`}
              data-testid="view-month-button"
            >
              <LayoutGrid className="w-4 h-4 mr-1" />
              Mes
            </Button>
          </div>
          <Button 
            onClick={openNewAppointmentModal}
            className="bg-[#1a5192] hover:bg-[#0d3a6e] text-white font-semibold shadow-lg"
            data-testid="new-appointment-button"
          >
            <Plus className="w-5 h-5 mr-2" strokeWidth={1.5} />
            Nueva Cita
          </Button>
        </div>
      </div>

      {/* Views */}
      {viewMode === 'day' && (
        <DayView
          currentDate={currentDate}
          selectedDate={selectedDate}
          appointments={appointments}
          selectedDateAppointments={selectedDateAppointments}
          onDayClick={handleDayClick}
          onAppointmentClick={openAppointmentDetail}
          onPrevMonth={handlePrevMonth}
          onNextMonth={handleNextMonth}
          onNewAppointment={openNewAppointmentModal}
          days={days}
          emptyDays={emptyDays}
        />
      )}

      {viewMode === 'week' && (
        <WeekView
          weekDays={weekDays}
          weekAppointments={weekAppointments}
          selectedDate={selectedDate}
          onDaySelect={handleDaySelect}
          onAppointmentClick={openAppointmentDetail}
          onAppointmentReschedule={handleDragReschedule}
          onPrevWeek={handlePrevWeek}
          onNextWeek={handleNextWeek}
          onToday={handleToday}
        />
      )}

      {viewMode === 'month' && (
        <MonthView
          currentDate={currentDate}
          days={days}
          emptyDays={emptyDays}
          monthAppointments={monthAppointments}
          selectedDate={selectedDate}
          onDaySelect={handleDaySelect}
          onAppointmentClick={openAppointmentDetail}
          onPrevMonth={handlePrevMonth}
          onNextMonth={handleNextMonth}
          onToday={handleToday}
        />
      )}

      {/* Modals */}
      <AppointmentDetail
        isOpen={isDetailModalOpen}
        onClose={() => setIsDetailModalOpen(false)}
        appointment={selectedAppointment}
        onEdit={openEditModal}
        onMarkCompleted={handleMarkCompleted}
        onCancel={openCancelDialog}
        onNoShow={handleNoShow}
        onReschedule={handleReschedule}
        onCreatePatientRecord={handleCreatePatientRecord}
      />

      <AppointmentModal
        isOpen={isModalOpen}
        onClose={() => { setIsModalOpen(false); setIsEditing(false); }}
        isEditing={isEditing}
        formData={formData}
        setFormData={setFormData}
        onSave={handleSubmit}
        saving={saving}
        calendarConnected={calendarConnected}
        token={token}
        examTypeNames={examTypeNames}
        examTypesData={examTypesData}
      />

      {/* Cancel Appointment Confirmation Dialog */}
      <AlertDialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Cancelar esta cita?</AlertDialogTitle>
            <AlertDialogDescription>
              {appointmentToCancel && (
                <>
                  Está a punto de cancelar la cita de <strong>{appointmentToCancel.patient_name}</strong>
                  {' '}programada para el{' '}
                  <strong>{format(new Date(appointmentToCancel.date), 'dd/MM/yyyy')}</strong>
                  {' '}a las <strong>{appointmentToCancel.hora_inicio}</strong>.
                  <br /><br />
                  Esta acción no se puede deshacer.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>No, mantener cita</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmCancelAppointment}
              className="bg-amber-500 hover:bg-amber-600"
            >
              Sí, cancelar cita
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Agenda;
